import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Section, Inner, Badge } from '../styles/v2.styled';
import useLMSAuth, { isInstructor } from '../hooks/useLMSAuth';
import SimulationStoryboardEditor from '../components/SimulationStoryboardEditor';
import { validateStoryboard } from '../utils/simulationStoryboard.cjs';

/**
 * 강의 등록 스튜디오.
 *
 * 과정 등록은 원래 목록 화면의 모달 하나였다. 제목·정원·가격만 받고 끝나서
 * "무엇을 가르치는지" 를 적을 자리가 없었고, 그래서 등록된 과정은 껍데기만
 * 남았다. 여기서는 순서대로 쌓게 한다 — 기본 정보를 저장해야 커리큘럼이
 * 열리고, 섹션이 있어야 차시를 쓸 수 있고, 차시가 있어야 공개할 수 있다.
 *
 * 각 단계는 저장되는 즉시 서버에 남는다. 마지막에 한꺼번에 저장하는 방식이면
 * 긴 본문을 쓰다 창을 닫았을 때 통째로 날아간다.
 */

const STEPS = [
  { key: 'basics', label: '기본 정보', hint: '과정의 얼굴이 되는 정보입니다' },
  { key: 'curriculum', label: '커리큘럼', hint: '무엇을 어떤 순서로 다룰지 나눕니다' },
  { key: 'lessons', label: '차시 작성', hint: '실제 강의 내용을 씁니다' },
  { key: 'publish', label: '공개', hint: '준비가 끝나면 수강생에게 엽니다' },
];

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[50] || '#f8fafc'};
`;
const Hero = styled.section`
  padding: 64px 0 36px;
  background: linear-gradient(135deg, #002d33, #0096c7);
  color: #fff;
  text-align: center;
`;
const HeroTitle = styled.h1`font-size: 2.1rem; margin: 0 0 10px; font-weight: 800;`;
const HeroSub = styled.p`font-size: 1rem; opacity: 0.85; margin: 0;`;

const Steps = styled.ol`
  display: flex; gap: 8px; list-style: none; padding: 0; margin: 0 0 28px;
  flex-wrap: wrap;
`;
const StepItem = styled.li`
  flex: 1 1 180px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? '#0096c7' : '#e5e7eb')};
  background: ${({ $active }) => ($active ? '#e8f7fc' : '#fff')};
  opacity: ${({ $enabled }) => ($enabled ? 1 : 0.5)};
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'not-allowed')};
`;
const StepNo = styled.div`
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
  color: ${({ $active }) => ($active ? '#0077a3' : '#9ca3af')};
`;
const StepLabel = styled.div`font-size: 1rem; font-weight: 700; color: #111827; margin-top: 2px;`;
const StepHint = styled.div`font-size: 0.78rem; color: #6b7280; margin-top: 4px;`;

const Panel = styled.div`
  background: #fff; border: 1px solid #e5e7eb; border-radius: 14px;
  padding: 28px; margin-bottom: 24px;
`;
const PanelTitle = styled.h2`font-size: 1.15rem; font-weight: 700; margin: 0 0 4px; color: #111827;`;
const PanelHint = styled.p`font-size: 0.86rem; color: #6b7280; margin: 0 0 20px;`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 2}, 1fr);
  gap: 16px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;
const Field = styled.div`margin-bottom: 16px;`;
const Label = styled.label`
  display: block; font-size: 0.84rem; font-weight: 600; margin-bottom: 6px; color: #374151;
`;
const Req = styled.span`color: #dc2626; margin-left: 3px;`;
const Input = styled.input`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 0.95rem; box-sizing: border-box;
`;
const TextArea = styled.textarea`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 0.95rem; box-sizing: border-box; min-height: ${({ $tall }) => ($tall ? '320px' : '90px')};
  font-family: inherit; line-height: 1.6; resize: vertical;
`;
const Select = styled.select`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 0.95rem; box-sizing: border-box;
`;
const Counter = styled.div`
  font-size: 0.75rem; color: ${({ $over }) => ($over ? '#dc2626' : '#9ca3af')};
  text-align: right; margin-top: 4px;
`;

const Actions = styled.div`display: flex; gap: 10px; flex-wrap: wrap; margin-top: 8px;`;
const PrimaryBtn = styled.button`
  padding: 11px 22px; font-size: 0.95rem; font-weight: 600; border-radius: 8px;
  background: linear-gradient(135deg, #0096c7, #005f7f);
  color: #fff; border: none; cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const GhostBtn = styled.button`
  padding: 11px 18px; font-size: 0.92rem; border-radius: 8px;
  border: 1px solid #d1d5db; background: #fff; color: #374151; cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const SmallBtn = styled.button`
  padding: 6px 12px; font-size: 0.78rem; border-radius: 6px;
  border: 1px solid ${({ $danger }) => ($danger ? '#fca5a5' : '#d1d5db')};
  background: ${({ $danger }) => ($danger ? '#fee2e2' : '#fff')};
  color: ${({ $danger }) => ($danger ? '#b91c1c' : '#374151')};
  cursor: pointer;
`;

const SectionCard = styled.div`
  border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px 18px; margin-bottom: 12px;
  background: ${({ $selected }) => ($selected ? '#f0fbff' : '#fff')};
`;
const SectionHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
`;
const SectionName = styled.div`font-size: 1rem; font-weight: 700; color: #111827;`;
const SectionMeta = styled.div`font-size: 0.78rem; color: #6b7280; margin-top: 2px;`;
const LessonRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 8px 10px; margin-top: 8px; border-radius: 7px;
  background: ${({ $selected }) => ($selected ? '#e8f7fc' : '#f9fafb')};
  cursor: pointer;
`;
const Empty = styled.div`
  padding: 28px; text-align: center; color: #9ca3af; font-size: 0.9rem;
  border: 1px dashed #d1d5db; border-radius: 10px;
`;
const Notice = styled.div`
  padding: 12px 14px; border-radius: 8px; margin-bottom: 18px; font-size: 0.88rem;
  background: ${({ $error }) => ($error ? '#fef2f2' : '#ecfdf5')};
  color: ${({ $error }) => ($error ? '#b91c1c' : '#047857')};
  border: 1px solid ${({ $error }) => ($error ? '#fecaca' : '#a7f3d0')};
`;

const MAX_BODY = 40000;
const emptyBasics = {
  title: '', instructor_id: '', category: '', description: '',
  thumbnail_url: '', video_url: '', capacity: 30, price: 0,
  simulation_key: '',
  simulation_storyboard: null,
  storyboard_revision: 0,
  start_date: '', end_date: '',
};

const json = async (url, options) => {
  const response = await fetch(url, { credentials: 'include', ...options });
  const text = await response.text();
  const parsed = text ? JSON.parse(text) : null;
  if (!response.ok) throw new Error(parsed?.error || `요청이 실패했습니다 (${response.status})`);
  return parsed;
};

export default function CourseStudioPage() {
  const { id: routeId } = useParams();
  const history = useHistory();
  const { role, loading } = useLMSAuth();

  const [courseId, setCourseId] = useState(routeId ? Number(routeId) : null);
  const [step, setStep] = useState('basics');
  const [basics, setBasics] = useState(emptyBasics);
  const [status, setStatus] = useState('open');
  const [instructors, setInstructors] = useState([]);
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [lessonDraft, setLessonDraft] = useState({ title: '', body: '', resource_url: '', duration_min: '' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const say = (message, error = false) => setNotice({ message, error });

  const lessonCount = useMemo(
    () => sections.reduce((total, section) => total + section.lessons.length, 0),
    [sections]
  );

  // 단계는 앞 단계가 끝나야 열린다. 커리큘럼 없이 차시를 쓰게 두면 붙일 곳이
  // 없고, 차시 없이 공개하면 수강생이 빈 과정을 받는다.
  const stepEnabled = useCallback((key) => {
    if (key === 'basics') return true;
    if (!courseId) return false;
    if (key === 'curriculum') return true;
    if (key === 'lessons') return sections.length > 0;
    return lessonCount > 0;
  }, [courseId, sections.length, lessonCount]);

  const loadCurriculum = useCallback(async (id) => {
    const data = await json(`/lms/courses/${id}/curriculum`);
    setSections(data.sections || []);
    return data.sections || [];
  }, []);

  useEffect(() => {
    json('/lms/instructors').then(setInstructors).catch(() => setInstructors([]));
  }, []);

  useEffect(() => {
    if (!routeId) return;
    const id = Number(routeId);
    (async () => {
      try {
        const course = await json(`/lms/courses/${id}`);
        setBasics({
          ...emptyBasics, ...course,
          instructor_id: course.instructor_id || '',
          start_date: course.start_date ? course.start_date.slice(0, 10) : '',
          end_date: course.end_date ? course.end_date.slice(0, 10) : '',
        });
        setStatus(course.status || 'open');
        setCourseId(id);
        await loadCurriculum(id);
        setStep('curriculum');
      } catch (error) {
        say(error.message, true);
      }
    })();
  }, [routeId, loadCurriculum]);

  const saveBasics = async () => {
    const storyboardError = validateStoryboard(basics.simulation_storyboard);
    if (storyboardError) { say(storyboardError, true); return; }
    if (!basics.title.trim()) { say('과정명은 반드시 필요합니다.', true); return; }
    setBusy(true);
    try {
      const payload = {
        ...basics,
        instructor_id: basics.instructor_id ? Number(basics.instructor_id) : null,
        capacity: Number(basics.capacity) || 30,
        price: Number(basics.price) || 0,
        start_date: basics.start_date || null,
        end_date: basics.end_date || null,
        // 정리가 끝나기 전에는 수강생에게 보이지 않아야 한다.
        status: courseId ? status : 'draft',
      };
      const saved = courseId
        ? await json(`/lms/courses/${courseId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        })
        : await json('/lms/courses', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      if (!courseId) {
        setCourseId(saved.id);
        setStatus(saved.status || 'draft');
        history.replace(`/CourseStudio/${saved.id}`);
      }
      setBasics(current=>({...current,...saved}));
      say('기본 정보를 저장했습니다.');
      setStep('curriculum');
    } catch (error) {
      say(error.message, true);
    } finally {
      setBusy(false);
    }
  };

  const addSection = async () => {
    const title = window.prompt('섹션 제목을 입력하세요. 예: 1장 — 환경 구성');
    if (!title || !title.trim()) return;
    setBusy(true);
    try {
      await json(`/lms/courses/${courseId}/sections`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim() }),
      });
      await loadCurriculum(courseId);
      say('섹션을 추가했습니다.');
    } catch (error) {
      say(error.message, true);
    } finally {
      setBusy(false);
    }
  };

  const renameSection = async (section) => {
    const title = window.prompt('섹션 제목', section.title);
    if (!title || !title.trim() || title === section.title) return;
    try {
      await json(`/lms/sections/${section.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim() }),
      });
      await loadCurriculum(courseId);
    } catch (error) {
      say(error.message, true);
    }
  };

  const removeSection = async (section) => {
    // 차시가 함께 사라진다는 것을 먼저 알린다. 확인 문구가 두루뭉술하면
    // 몇 시간 쓴 본문이 조용히 날아간다.
    const warning = section.lessons.length
      ? `"${section.title}" 을 지우면 차시 ${section.lessons.length}개도 함께 사라집니다. 계속할까요?`
      : `"${section.title}" 을 지울까요?`;
    if (!window.confirm(warning)) return;
    try {
      await json(`/lms/sections/${section.id}`, { method: 'DELETE' });
      if (activeSectionId === section.id) { setActiveSectionId(null); setActiveLessonId(null); }
      await loadCurriculum(courseId);
      say('섹션을 삭제했습니다.');
    } catch (error) {
      say(error.message, true);
    }
  };

  const startNewLesson = (sectionId) => {
    setActiveSectionId(sectionId);
    setActiveLessonId(null);
    setLessonDraft({ title: '', body: '', resource_url: '', duration_min: '' });
    setStep('lessons');
  };

  const openLesson = async (lesson) => {
    try {
      const full = await json(`/lms/lessons/${lesson.id}`);
      setActiveSectionId(full.section_id);
      setActiveLessonId(full.id);
      setLessonDraft({
        title: full.title || '',
        body: full.body || '',
        resource_url: full.resource_url || '',
        duration_min: full.duration_min || '',
      });
      setStep('lessons');
    } catch (error) {
      say(error.message, true);
    }
  };

  const saveLesson = async () => {
    if (!lessonDraft.title.trim()) { say('차시 제목은 반드시 필요합니다.', true); return; }
    if (lessonDraft.body.length > MAX_BODY) { say(`본문은 ${MAX_BODY}자를 넘을 수 없습니다.`, true); return; }
    setBusy(true);
    try {
      const payload = {
        title: lessonDraft.title.trim(),
        body: lessonDraft.body,
        resource_url: lessonDraft.resource_url.trim() || null,
        duration_min: lessonDraft.duration_min ? Number(lessonDraft.duration_min) : null,
      };
      if (activeLessonId) {
        await json(`/lms/lessons/${activeLessonId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      } else {
        const created = await json(`/lms/sections/${activeSectionId}/lessons`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        setActiveLessonId(created.id);
      }
      await loadCurriculum(courseId);
      say('차시를 저장했습니다.');
    } catch (error) {
      say(error.message, true);
    } finally {
      setBusy(false);
    }
  };

  const removeLesson = async (lesson) => {
    if (!window.confirm(`"${lesson.title}" 차시를 지울까요?`)) return;
    try {
      await json(`/lms/lessons/${lesson.id}`, { method: 'DELETE' });
      if (activeLessonId === lesson.id) {
        setActiveLessonId(null);
        setLessonDraft({ title: '', body: '', resource_url: '', duration_min: '' });
      }
      await loadCurriculum(courseId);
    } catch (error) {
      say(error.message, true);
    }
  };

  const changeStatus = async (next) => {
    setBusy(true);
    try {
      await json(`/lms/courses/${courseId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      setStatus(next);
      say(next === 'open' ? '과정을 공개했습니다.' : '과정을 비공개로 되돌렸습니다.');
    } catch (error) {
      say(error.message, true);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageWrapper><Section><Inner>불러오는 중…</Inner></Section></PageWrapper>;

  if (!isInstructor(role)) {
    return (
      <PageWrapper>
        <Hero>
          <HeroTitle>강의 등록</HeroTitle>
          <HeroSub>강사 또는 관리자만 이용할 수 있습니다</HeroSub>
        </Hero>
        <Section><Inner>
          <Empty>
            강의를 등록하려면 강사 권한이 필요합니다.
            <div style={{ marginTop: 14 }}><Link to="/Courses"><GhostBtn>과정 목록으로</GhostBtn></Link></div>
          </Empty>
        </Inner></Section>
      </PageWrapper>
    );
  }

  const activeSection = sections.find((s) => s.id === activeSectionId) || null;

  return (
    <PageWrapper>
      <Hero>
        <HeroTitle>강의 등록 스튜디오</HeroTitle>
        <HeroSub>기본 정보 → 커리큘럼 → 차시 → 공개, 순서대로 쌓아 갑니다</HeroSub>
      </Hero>

      <Section>
        <Inner>
          {notice && <Notice $error={notice.error}>{notice.message}</Notice>}

          <Steps>
            {STEPS.map((entry, index) => {
              const enabled = stepEnabled(entry.key);
              return (
                <StepItem
                  key={entry.key}
                  $active={step === entry.key}
                  $enabled={enabled}
                  onClick={() => enabled && setStep(entry.key)}
                >
                  <StepNo $active={step === entry.key}>STEP {index + 1}</StepNo>
                  <StepLabel>{entry.label}</StepLabel>
                  <StepHint>{entry.hint}</StepHint>
                </StepItem>
              );
            })}
          </Steps>

          {step === 'basics' && (
            <Panel>
              <PanelTitle>기본 정보</PanelTitle>
              <PanelHint>
                여기서 저장해야 커리큘럼을 짤 수 있습니다. 저장하면 비공개(draft) 상태로 남으니
                정리가 끝날 때까지 수강생에게 보이지 않습니다.
              </PanelHint>

              <Field>
                <Label>과정명<Req>*</Req></Label>
                <Input
                  value={basics.title}
                  onChange={(e) => setBasics({ ...basics, title: e.target.value })}
                  placeholder="예: 실무 React 마스터"
                />
              </Field>

              <FieldRow>
                <Field>
                  <Label>담당 강사</Label>
                  <Select
                    value={basics.instructor_id}
                    onChange={(e) => setBasics({ ...basics, instructor_id: e.target.value })}
                  >
                    <option value="">선택 안 함</option>
                    {instructors.map((i) => (
                      <option key={i.id} value={i.id}>{i.name}{i.title ? ` — ${i.title}` : ''}</option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>분류</Label>
                  <Input
                    value={basics.category || ''}
                    onChange={(e) => setBasics({ ...basics, category: e.target.value })}
                    placeholder="예: Frontend, DevOps, AI"
                  />
                </Field>
              </FieldRow>

              <Field>
                <Label>소개</Label>
                <TextArea
                  value={basics.description || ''}
                  onChange={(e) => setBasics({ ...basics, description: e.target.value })}
                  placeholder="이 과정을 마치면 무엇을 할 수 있게 되는지 적어 주세요."
                />
              </Field>

              <FieldRow>
                <Field>
                  <Label>썸네일 URL</Label>
                  <Input
                    value={basics.thumbnail_url || ''}
                    onChange={(e) => setBasics({ ...basics, thumbnail_url: e.target.value })}
                    placeholder="https://…"
                  />
                </Field>
                <Field>
                  <Label>대표 영상 URL</Label>
                  <Input
                    value={basics.video_url || ''}
                    onChange={(e) => setBasics({ ...basics, video_url: e.target.value })}
                    placeholder="https://…"
                  />
                </Field>
              </FieldRow>

              <Field>
                <Label>수업 방식</Label>
                <Select
                  value={basics.simulation_key || ''}
                  onChange={(e) => setBasics({ ...basics, simulation_key: e.target.value })}
                >
                  <option value="">영상/본문 수업</option>
                  <option value="campus">앤츠 시뮬레이션 캠퍼스 전체</option>
                  <option value="semiconductor">반도체 제조 Fab</option>
                  <option value="router">공유기 제조</option>
                  <option value="infra">인프라 구축</option>
                  <option value="robot">로봇 생산</option>
                </Select>
              </Field>

              {(basics.simulation_key || basics.simulation_storyboard) && <SimulationStoryboardEditor
                value={basics.simulation_storyboard}
                courseId={courseId}
                title={basics.title}
                onChange={(simulation_storyboard) => setBasics({ ...basics, simulation_storyboard })}
              />}

              <FieldRow $cols={4}>
                <Field>
                  <Label>정원</Label>
                  <Input
                    type="number" min="1"
                    value={basics.capacity}
                    onChange={(e) => setBasics({ ...basics, capacity: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>수강료</Label>
                  <Input
                    type="number" min="0"
                    value={basics.price}
                    onChange={(e) => setBasics({ ...basics, price: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>시작일</Label>
                  <Input
                    type="date"
                    value={basics.start_date}
                    onChange={(e) => setBasics({ ...basics, start_date: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>종료일</Label>
                  <Input
                    type="date"
                    value={basics.end_date}
                    onChange={(e) => setBasics({ ...basics, end_date: e.target.value })}
                  />
                </Field>
              </FieldRow>

              <Actions>
                <PrimaryBtn onClick={saveBasics} disabled={busy}>
                  {courseId ? '기본 정보 저장' : '저장하고 커리큘럼 짜기'}
                </PrimaryBtn>
                <Link to="/Courses"><GhostBtn type="button">과정 목록</GhostBtn></Link>
              </Actions>
            </Panel>
          )}

          {step === 'curriculum' && (
            <Panel>
              <PanelTitle>커리큘럼</PanelTitle>
              <PanelHint>
                다룰 내용을 섹션으로 나눕니다. 섹션 하나가 보통 한 주 또는 한 장에 해당합니다.
              </PanelHint>

              {sections.length === 0 ? (
                <Empty>
                  아직 섹션이 없습니다. 첫 섹션을 추가해 목차를 시작하세요.
                  <div style={{ marginTop: 14 }}><PrimaryBtn onClick={addSection} disabled={busy}>+ 섹션 추가</PrimaryBtn></div>
                </Empty>
              ) : (
                <>
                  {sections.map((section) => (
                    <SectionCard key={section.id} $selected={section.id === activeSectionId}>
                      <SectionHead>
                        <div>
                          <SectionName>{section.section_order}. {section.title}</SectionName>
                          <SectionMeta>차시 {section.lessons.length}개</SectionMeta>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <SmallBtn onClick={() => startNewLesson(section.id)}>+ 차시</SmallBtn>
                          <SmallBtn onClick={() => renameSection(section)}>이름 변경</SmallBtn>
                          <SmallBtn $danger onClick={() => removeSection(section)}>삭제</SmallBtn>
                        </div>
                      </SectionHead>
                      {section.lessons.map((lesson) => (
                        <LessonRow key={lesson.id} onClick={() => openLesson(lesson)}>
                          <span>{lesson.lesson_order}. {lesson.title}</span>
                          <span style={{ fontSize: '0.76rem', color: '#6b7280' }}>
                            {lesson.duration_min ? `${lesson.duration_min}분` : ''}
                          </span>
                        </LessonRow>
                      ))}
                    </SectionCard>
                  ))}
                  <Actions>
                    <PrimaryBtn onClick={addSection} disabled={busy}>+ 섹션 추가</PrimaryBtn>
                    <GhostBtn onClick={() => setStep('basics')}>기본 정보로</GhostBtn>
                    <GhostBtn onClick={() => setStep('publish')} disabled={lessonCount === 0}>
                      공개 단계로
                    </GhostBtn>
                  </Actions>
                </>
              )}
            </Panel>
          )}

          {step === 'lessons' && (
            <Panel>
              <PanelTitle>
                차시 작성
                {activeSection && <Badge style={{ marginLeft: 8 }}>{activeSection.title}</Badge>}
              </PanelTitle>
              <PanelHint>
                {activeLessonId ? '기존 차시를 고치는 중입니다.' : '새 차시를 씁니다.'}
                {' '}저장은 이 화면에서 바로 반영됩니다.
              </PanelHint>

              {!activeSectionId ? (
                <Empty>
                  먼저 커리큘럼에서 차시를 붙일 섹션을 고르세요.
                  <div style={{ marginTop: 14 }}><GhostBtn onClick={() => setStep('curriculum')}>커리큘럼으로</GhostBtn></div>
                </Empty>
              ) : (
                <>
                  <Field>
                    <Label>차시 제목<Req>*</Req></Label>
                    <Input
                      value={lessonDraft.title}
                      onChange={(e) => setLessonDraft({ ...lessonDraft, title: e.target.value })}
                      placeholder="예: 훅으로 상태 나누기"
                    />
                  </Field>

                  <FieldRow>
                    <Field>
                      <Label>자료·영상 링크</Label>
                      <Input
                        value={lessonDraft.resource_url}
                        onChange={(e) => setLessonDraft({ ...lessonDraft, resource_url: e.target.value })}
                        placeholder="https://…"
                      />
                    </Field>
                    <Field>
                      <Label>예상 소요(분)</Label>
                      <Input
                        type="number" min="1"
                        value={lessonDraft.duration_min}
                        onChange={(e) => setLessonDraft({ ...lessonDraft, duration_min: e.target.value })}
                      />
                    </Field>
                  </FieldRow>

                  <Field>
                    <Label>강의 본문</Label>
                    <TextArea
                      $tall
                      value={lessonDraft.body}
                      onChange={(e) => setLessonDraft({ ...lessonDraft, body: e.target.value })}
                      placeholder="마크다운으로 작성할 수 있습니다."
                    />
                    <Counter $over={lessonDraft.body.length > MAX_BODY}>
                      {lessonDraft.body.length.toLocaleString()} / {MAX_BODY.toLocaleString()}자
                    </Counter>
                  </Field>

                  <Actions>
                    <PrimaryBtn onClick={saveLesson} disabled={busy}>차시 저장</PrimaryBtn>
                    <GhostBtn onClick={() => startNewLesson(activeSectionId)}>새 차시</GhostBtn>
                    <GhostBtn onClick={() => setStep('curriculum')}>커리큘럼으로</GhostBtn>
                    {activeLessonId && (
                      <SmallBtn
                        $danger
                        onClick={() => {
                          const lesson = activeSection?.lessons.find((l) => l.id === activeLessonId);
                          if (lesson) removeLesson(lesson);
                        }}
                      >
                        이 차시 삭제
                      </SmallBtn>
                    )}
                  </Actions>
                </>
              )}
            </Panel>
          )}

          {step === 'publish' && (
            <Panel>
              <PanelTitle>공개</PanelTitle>
              <PanelHint>수강생에게 열기 전에 마지막으로 확인하세요.</PanelHint>

              <SectionCard>
                <SectionName>{basics.title}</SectionName>
                <SectionMeta>
                  섹션 {sections.length}개 · 차시 {lessonCount}개 · 정원 {basics.capacity}명
                  {' · '}현재 상태 <strong>{status === 'open' ? '공개' : status === 'draft' ? '비공개' : status}</strong>
                </SectionMeta>
              </SectionCard>

              {lessonCount === 0 && (
                <Notice $error>차시가 하나도 없습니다. 빈 과정을 공개하면 수강생이 받을 것이 없습니다.</Notice>
              )}

              <Actions>
                {status === 'open' ? (
                  <GhostBtn onClick={() => changeStatus('draft')} disabled={busy}>비공개로 되돌리기</GhostBtn>
                ) : (
                  <PrimaryBtn onClick={() => changeStatus('open')} disabled={busy || lessonCount === 0}>
                    수강생에게 공개
                  </PrimaryBtn>
                )}
                <GhostBtn onClick={() => setStep('curriculum')}>커리큘럼으로</GhostBtn>
                <Link to="/Courses"><GhostBtn type="button">과정 목록</GhostBtn></Link>
              </Actions>
            </Panel>
          )}
        </Inner>
      </Section>
    </PageWrapper>
  );
}
